import { Card, CardContent, Grid, makeStyles, Tab, Tabs, Typography } from '@material-ui/core';
import { formatLocalDate } from '../../core';
import { AuditGroup, auditSummaries, AuditSummary } from '../../core/audit';
import { auth } from '../../core/auth';
import {
  Audit,
  ChangeReason,
  Diagnosis,
  Disagreement,
  disagreementToString,
  DownCode,
  LostRevenue,
  NCNBReason,
  Procedure,
  ReturnItem,
  TrackingCode,
  Worksheet,
} from '../../core/domain';
import { LabelItem, splitCamelCase, Stringify, stringifyProcedure } from '../../core/format';

import { useMemo, useState } from 'react';
import { AuditRebuttalDialogue } from '../../core/audit-rebuttal/audit-rebuttal';
import { RebuttalAction } from '../../core/audit-rebuttal/audit-rebuttal-model';
import { RebuttalHistory } from '../../core/audit-rebuttal/rebuttal-history';
import { gray, mediumGray, orange, originalOrange, orignalGreen } from '../../core/theme';
import { VisitCode, WorksheetApis } from '../../core/visit-context';
import { elementCode, WorksheetElement } from '../../core/worksheets';
import { SummaryActions } from '../coding-tool/coding-actions';

const stringify: Stringify<Procedure> = stringifyProcedure({});

function ChangeReasonView(v: ChangeReason) {
  const valueLabel = stringify[v.fieldName] || ((v: any) => JSON.stringify(v));
  const valueStyle = {
    textAlign: 'right',
    color: gray,
    width: 80,
    fontSize: 12,
  };
  return (
    <Grid item container key={v.fieldName} spacing={2} direction="row" alignItems="flex-end" justify="center" style={{ width: '100%', flexWrap: 'nowrap', padding: 0 }}>
      <Grid item xs={2} style={{ overflowX: 'hidden' }}>
        <Typography
          variant="body2"
          style={{
            color: orange,
            width: '100%',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflowX: 'hidden',
            fontSize: 12,
          }}
        >
          {splitCamelCase(v.fieldName)}
        </Typography>
      </Grid>

      {LabelItem('Old Value: ', { maxWidth: 120, fontSize: 12 }, NaN)}
      {LabelItem(valueLabel(v.oldValue), valueStyle, 3)}

      {LabelItem('New Value : ', { maxWidth: 120, fontSize: 12 }, NaN)}
      {LabelItem(valueLabel(v.newValue), valueStyle, 3)}
    </Grid>
  );
}

function ChangeHistory(a: Audit) {
  if (!a || !a.changeReasons?.length) {
    return null;
  }
  const reasons = a.changeReasons.filter((v) => v.fieldName !== 'object');

  if (!reasons.length) {
    return null;
  }

  return (
    <div>
      <br />
      {reasons.map((v, i) => (
        <ChangeReasonView key={i} {...v} />
      ))}
      <br />
    </div>
  );
}

const cardStyles = makeStyles({
  root: {
    width: '100%',
    minWidth: 180,
  },
  header: {
    fontSize: 12,
  },
  bullet: {
    display: 'inline-block',
    margin: '0 2px',
    transform: 'scale(0.8)',
    color: mediumGray,
  },
  light: {
    color: mediumGray,
  },
  message: {
    paddingTop: '12px',
  },
});

const row: any = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'nowrap',
  alignItems: 'center',
  width: '100%',
  paddingRight: '0px',
};

const hr = <hr style={{ border: 0, width: '100%', height: 1, backgroundColor: '#ddd' }} />;

type AuditActionProps = {
  e: WorksheetElement | Audit | any;
  code: string;
  workflow: string;
  rebuttal?: RebuttalAction;
  worksheet?: Worksheet;
  reasons?: VisitCode[];
  currentWorkflow?: string;
};

function AuditAction({ e, code, workflow, rebuttal, worksheet, reasons, currentWorkflow }: AuditActionProps) {
  const isAudit = !!e.changeReasonCode;
  const auditRef = e?.audit || (e as Audit);
  let audit = auditRef;
  let excludeFromAudit = false;

  if (e.auditMap) {
    const map = e.auditMap[workflow];
    if (map) {
      audit = map;
    }
  }
  excludeFromAudit = audit?.excludeFromAudit;

  const { action, notes } = audit;
  const author = audit?.createdBy?.firstName?.length > 0 ? audit.createdBy : auth.signedIn;

  let person = '';
  if (author?.firstName) {
    person = `${author.firstName} ${author.lastName}`;
  }

  let changeReasonDescription = audit?.changeReasonDescription;
  if (!changeReasonDescription && audit?.changeReasons?.length) {
    changeReasonDescription = audit?.changeReasons[audit?.changeReasons.length - 1].changeReasonDescription;
  }

  audit.workflow = workflow;

  const [disagreements, setDisagreements] = useState((auditRef.disagreementReasons || []).filter((v) => !!v.createDate));

  console.info('rebuttalReasons', disagreements);
  const onActionComplete = () => {
    setDisagreements((auditRef.disagreementReasons || []).filter((v) => !!v?.createDate));
  };

  function rebuttalName(d: Disagreement) {
    if (!d?.disagreementBy) {
      return person;
    }
    return `${d.disagreementBy.firstName} ${d.disagreementBy.lastName}`;
  }
  return (
    <>
      <div style={row}>
        <Typography
          color="textPrimary"
          style={{
            textDecoration: isAudit ? undefined : e?.active ? undefined : 'line-through',
          }}
        >
          {code}
        </Typography>

        {excludeFromAudit && (
          <Typography color="textSecondary" style={{ fontStyle: 'italic', paddingLeft: 8, paddingTop: 4 }} gutterBottom>
            * Education Only
          </Typography>
        )}

        <div style={{ flexGrow: 1 }} />
        <Typography color="textSecondary" style={{ paddingLeft: 12, whiteSpace: 'nowrap' }}>
          {action || ''}
          {changeReasonDescription ? ' - ' + changeReasonDescription : null}
        </Typography>

        {rebuttal && <div style={{ flexGrow: 0.3 }} />}
        {rebuttal && (
          <RebuttalHistory
            onActionComplete={onActionComplete}
            action={rebuttal as any}
            workflow={currentWorkflow}
            worksheet={worksheet}
            audit={auditRef}
            map={audit}
            reasons={reasons}
            user={auth.signedIn}
          />
        )}
      </div>

      <ChangeHistory {...audit} />
      {(notes || person?.length > 0) && (
        <div style={row}>
          <Typography color="textSecondary" style={{ fontStyle: 'italic' }} gutterBottom>
            {notes || ''}
          </Typography>
          <div style={{ flexGrow: 1 }} />
          <span style={{ color: mediumGray }}> {person}</span>
        </div>
      )}
      {rebuttal &&
        disagreements.map((d: Disagreement, i) =>
          d.agree ? (
            <div style={row}>
              <Typography style={{ fontStyle: 'italic', color: orignalGreen }} gutterBottom>
                {disagreementToString(i + 1, d)}
              </Typography>
              <div style={{ flexGrow: 1 }} />
              <span style={{ color: orignalGreen }}> {rebuttalName(d)}</span>
            </div>
          ) : (
            <div style={row}>
              <Typography color="primary" style={{ fontStyle: 'italic' }} gutterBottom>
                {disagreementToString(i + 1, d)}
              </Typography>
              <div style={{ flexGrow: 1 }} />
              <span style={{ color: originalOrange }}> {rebuttalName(d)}</span>
            </div>
          )
        )}
    </>
  );
}

type AuditGroupViewProps = {
  title: string;
  g: AuditGroup;
  code: (e: any) => string;
  rebuttal?: RebuttalAction;
  worksheet?: Worksheet;
  reasons?: VisitCode[];
  currentWorkflow?: string;
};

function AuditGroupView({ title, g, code, rebuttal, worksheet, reasons, currentWorkflow }: AuditGroupViewProps) {
  const { added, deleted, moved, revised, show, workflow } = g;

  return (
    show && (
      <div>
        <div style={{ ...row, ...{ paddingBottom: 6 } }}>
          <Typography
            style={{
              fontSize: 14,
              color: mediumGray,
              whiteSpace: 'nowrap',
              paddingRight: 6,
            }}
            color="textPrimary"
          >
            {title}
          </Typography>
          {hr}
        </div>

        {added?.length
          ? added.map((v) => <AuditAction e={v} code={code(v)} workflow={workflow} rebuttal={rebuttal} worksheet={worksheet} reasons={reasons} currentWorkflow={currentWorkflow} />)
          : null}
        {deleted?.length
          ? deleted.map((v) => (
              <AuditAction e={v} code={code(v)} workflow={workflow} rebuttal={rebuttal} worksheet={worksheet} reasons={reasons} currentWorkflow={currentWorkflow} />
            ))
          : null}
        {moved?.length
          ? moved.map((v) => <AuditAction e={v} code={code(v)} workflow={workflow} rebuttal={rebuttal} worksheet={worksheet} reasons={reasons} currentWorkflow={currentWorkflow} />)
          : null}
        {revised?.length
          ? revised.map((v) => (
              <AuditAction e={v} code={code(v)} workflow={workflow} rebuttal={rebuttal} worksheet={worksheet} reasons={reasons} currentWorkflow={currentWorkflow} />
            ))
          : null}
        <br />
      </div>
    )
  );
}

function ActivitySummary(summary: AuditSummary) {
  const classes = cardStyles();
  const { icd, cpt, downCodes, lostRevenue, tracking, sendBacks, show, reasonForVisit, ncnbReasons, worksheet, workflow, currentWorkflow, rebuttal, config } = summary;

  const { serviceDate } = worksheet;
  const title = `REVIEW / AUDIT SUMMARY - ${workflow} - ${formatLocalDate(serviceDate)}`;

  const { auditICD, auditCPT, auditTracking, auditDownCode, auditLostRevenue, auditSendBack, auditNCNB } = config;

  return (
    show && (
      <Grid container item direction="column" spacing={1} style={{ width: '98%' }}>
        <Grid item style={{ paddingLeft: 0, paddingTop: 12 }}>
          <Typography variant="subtitle2" style={{ padding: '0px' }}>
            {title}
          </Typography>
        </Grid>
        {
          <Grid
            item
            style={{
              paddingLeft: '0px'
            }}
          >
            <Card className={classes.root}>
              <CardContent>
                {
                  <AuditGroupView
                    title={'Diagnosis Changes'}
                    g={icd}
                    code={(v: Diagnosis) => v.diagnosisCode || elementCode(v)}
                    rebuttal={rebuttal}
                    worksheet={worksheet}
                    reasons={auditICD}
                    currentWorkflow={currentWorkflow}
                  />
                }
                {
                  <AuditGroupView
                    title={'Reason for Visit'}
                    g={reasonForVisit}
                    code={(v: Diagnosis) => v.diagnosisCode}
                    rebuttal={rebuttal}
                    worksheet={worksheet}
                    reasons={auditICD}
                    currentWorkflow={currentWorkflow}
                  />
                }
                {
                  <AuditGroupView
                    title={'Procedure Changes'}
                    g={cpt}
                    code={(v: Procedure) => v.cptCode || elementCode(v)}
                    rebuttal={rebuttal}
                    worksheet={worksheet}
                    reasons={auditCPT}
                    currentWorkflow={currentWorkflow}
                  />
                }
                {
                  <AuditGroupView
                    title={'Down Code Changes'}
                    g={downCodes}
                    code={(v: DownCode) => v.actualCPTCode}
                    rebuttal={rebuttal}
                    worksheet={worksheet}
                    reasons={auditDownCode}
                    currentWorkflow={currentWorkflow}
                  />
                }
                {
                  <AuditGroupView
                    title={'Lost Revenue Changes'}
                    g={lostRevenue}
                    code={(v: LostRevenue) => v.cptCode}
                    rebuttal={rebuttal}
                    worksheet={worksheet}
                    reasons={auditLostRevenue}
                    currentWorkflow={currentWorkflow}
                  />
                }
                {
                  <AuditGroupView
                    title={'Tracking Code Changes'}
                    g={tracking}
                    code={(v: TrackingCode) => v.code}
                    rebuttal={rebuttal}
                    worksheet={worksheet}
                    reasons={auditTracking}
                    currentWorkflow={currentWorkflow}
                  />
                }
                {
                  <AuditGroupView
                    title={'Send Back Changes'}
                    g={sendBacks}
                    code={(v: ReturnItem) => v.code}
                    rebuttal={rebuttal}
                    worksheet={worksheet}
                    reasons={auditSendBack}
                    currentWorkflow={currentWorkflow}
                  />
                }
                {
                  <AuditGroupView
                    title={'No Code / No Bill Changes'}
                    g={ncnbReasons}
                    code={(v: NCNBReason) => v.reasonCode}
                    rebuttal={rebuttal}
                    worksheet={worksheet}
                    reasons={auditNCNB}
                    currentWorkflow={currentWorkflow}
                  />
                }
              </CardContent>
            </Card>
          </Grid>
        }
      </Grid>
    )
  );
}
type SummaryProps = {
  apis?: WorksheetApis[];
  workflows?: string[];
};

const containerStyle = {
  width: '100%',
  minHeight: '100%',
};
export function Summary(props: SummaryProps) {
  const { apis, workflows } = props;

  const summaries = auditSummaries(apis, workflows);
    // Memoize summaries and worksheet type mapping
  //const summaries = useMemo(() => auditSummaries(apis, workflows), [apis, workflows]);
  const worksheetTypeMap = useMemo(() => {
    return summaries.reduce((acc, summary) => {
      const type = summary.worksheet.worksheetType || 'Unknown';
      if (!acc[type]) acc[type] = [];
      acc[type].push(summary);
      return acc;
    }, {} as Record<string, AuditSummary[]>);
  }, [summaries]);
  const worksheetTypes = useMemo(() => Object.keys(worksheetTypeMap), [worksheetTypeMap]);

  const showActions = apis.length === 1;
  const summaryApis = apis[0];
  const [tabIndex, setTabIndex] = useState(0);
  const handleTabChange = (_: React.ChangeEvent<{}>, newIndex: number) => {
    setTabIndex(newIndex);
  };
  return (
    <>
      <div style={containerStyle}>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          style={{ marginBottom: 16 }}
        >
          {worksheetTypes.map((type, idx) => (
            <Tab key={type} label={type} />
          ))}
        </Tabs>
        <Grid
          key={worksheetTypes[tabIndex]} // <-- This forces remount on tab change
          container
          direction="row"
          spacing={2}
          style={{
            width: '75vw',
            marginLeft: 12,
            maxHeight: '80vh',
            overflowY: 'auto',
            paddingBottom: showActions ? 100 : 12
          }}
        >
          {worksheetTypeMap[worksheetTypes[tabIndex]]?.map((v) => (
            <ActivitySummary key={v.worksheet.worksheetId} {...v} />
          ))}
        </Grid>
        {showActions && (
          <div style={{ position: 'absolute', width: '72.5vw', bottom: 12, paddingTop: 12, paddingBottom: 12, marginLeft: 16 }}>
              <SummaryActions {...summaryApis} />
          </div>
        )}
      </div>
      <AuditRebuttalDialogue />
    </>
  );
}
